import { ActionTree } from "vuex";
import {
	VideoReportingState,
	VideoReportRequest,
	VideoReportingStates,
	CameraRecordingState,
	DeviceCamera
} from "./types";
import { RootState } from "@/store/types";
import videoReportingApi from "@/services/api.videoReporting";
import { VideoReportRecordingService } from "@/services/videoReportRecordingService";
import PromisePool from "es6-promise-pool";

interface RecordableCamera {
	parentDeviceIndex: number;
	parentDeviceId: number;
	camera: DeviceCamera;
	duration: number;
	date: Date;
}

export const actions: ActionTree<VideoReportingState, RootState> = {
	async fetchVideoReportingPrefs({ commit }) {
		let prefs = await videoReportingApi.getVideoReportingPrefs();
		commit("setVideoReportingPrefs", prefs);
	},

	async processReport({ commit, state, rootGetters }, reportRequest: VideoReportRequest) {
		// Update our state to show the processing banner
		commit("setVideoReportingState", VideoReportingStates.ProcessingReport);

		// Create a new event which will hold this video report
		const reportEventId = await videoReportingApi.createReport(reportRequest);
		if (!reportEventId) {
			throw new Error("Report could not be created.");
		}

		commit("setCurrentReportId", reportRequest.videoReportId);
		commit("setCurrentReportEventId", reportEventId);

		// Create an instance of the video report recording service
		const reportService = new VideoReportRecordingService(reportEventId);

		// Build a list of all the cameras to record
		const camerasToRecord: RecordableCamera[] = reportRequest.search.flatMap((deviceSearch, deviceIndex) => {
			return deviceSearch.cameras.map(camera => ({
				parentDeviceIndex: deviceIndex,
				parentDeviceId: deviceSearch.deviceId,
				duration: deviceSearch.maxDuration,
				date: deviceSearch.date,
				camera
			}));
		});

		const reviewsCompleted = {};
		const deviceEventRecords = {};

		// Create event records for all our top-level devices
		await Promise.all(
			reportRequest.search.map(async (deviceSearch, deviceIndex) => {
				let deviceRecordId = await videoReportingApi.createDeviceReportRecord(
					reportEventId,
					deviceSearch.deviceId
				);
				deviceEventRecords[deviceIndex] = deviceRecordId;
			})
		);

		// Store our device event record id's in the store for use when downloading the report
		commit("setDeviceEventRecords", deviceEventRecords);

		// Defines a method which records a camera using the Video Reporting recording service.
		const recordCamera = async (cameraToRecord: RecordableCamera) => {
			// Set our audit endpoint to the endpoint for this camera
			commit("setAuditEndpoint", cameraToRecord.camera["auditServiceEndpoint"]);

			// Get a session id for the device service, and use it to record the camera
			let authSessionId = rootGetters["sessions/getSession"](3);
			let { state, messages } = await reportService.recordCamera(
				{
					cameraId: cameraToRecord.camera.deviceID,
					recordingDate: cameraToRecord.date,
					duration: cameraToRecord.duration ?? reportRequest.cameraSettings.maxDuration,
					eventRecordId: deviceEventRecords[cameraToRecord.parentDeviceIndex]
				},
				authSessionId
			);

			// Create our camera report record. If the recording was successful we don't need to send any logs.
			let cameraEventRecordId = await videoReportingApi.createCameraReportRecord(
				cameraToRecord.camera.deviceID,
				reportEventId,
				state == CameraRecordingState.Success ? [] : messages,
				state
			);

			cameraToRecord.camera.successState = state;
			cameraToRecord.camera.cameraEventRecordId = cameraEventRecordId;

			commit("cameraComplete");
		};

		// Processes the next camera in the list of cameras to record
		const processNextCamera = async () => {
			const cameraToRecord = camerasToRecord.shift();
			await recordCamera(cameraToRecord);

			// If there are no more cameras in the list of cameras to record that belong to the same
			// parent device as the current camera, and we haven't already marked the review for the parent
			// device as complete, then mark it as complete and commit a reviewComplete mutation.
			if (
				!camerasToRecord.some(camera => camera.parentDeviceIndex == cameraToRecord.parentDeviceIndex) &&
				!reviewsCompleted[cameraToRecord.parentDeviceIndex]
			) {
				reviewsCompleted[cameraToRecord.parentDeviceIndex] = true;
				commit("reviewComplete");
			}
		};

		// Create a new promise pool which will record our cameras asynchronously in batches (the
		// max number of cameras recording at once is defined by the Max Workers pref). If we've
		// run out of cameras to record, we return null (indicating there are no more tasks).
		var pool = new PromisePool(() => {
			if (camerasToRecord.length == 0) {
				return null;
			}

			return processNextCamera();
		}, state.videoReportingPrefs.maxWorkers || 10);

		// Start processing the cameras.
		await pool.start();

		return reportRequest;
	}
};
